import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@kidshome/ui';
import { ToastContext, type ToastApi, type ToastTone } from '@/shared/ui/toast/toast-context';

/**
 * Lightweight, dependency-free toast system (FSD `shared`). A provider owns the queue + renders a
 * fixed, screen-reader-announced viewport; `useToast()` (in `toast-context.ts`) exposes
 * `success`/`error` so any feature can surface a kind confirmation or an error that would otherwise be
 * swallowed.
 *
 * Accessibility:
 *  - success toasts live in a `role="status"` / `aria-live="polite"` region (non-interrupting),
 *  - error toasts live in a `role="alert"` / `aria-live="assertive"` region (interrupts), and each
 *    carries a manual dismiss button.
 *  - auto-dismiss after a tone-dependent delay; errors linger longer so they're not missed.
 *  - the entrance animation is collapsed to ~0ms under `prefers-reduced-motion` (global base rule).
 * Tokens only (both themes); copy is passed in by callers as already-translated strings (ADR-0025).
 */

interface Toast {
  readonly id: number;
  readonly tone: ToastTone;
  readonly message: string;
}

/** Auto-dismiss delays (ms): errors linger longer so they aren't missed. */
const DISMISS_MS: Record<ToastTone, number> = { success: 4000, error: 7000 };

export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { t } = useTranslation('common');
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  // Monotonic id source (a ref so it survives re-renders without re-creating the callbacks).
  const nextId = useRef(0);

  const dismiss = useCallback((id: number): void => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string): void => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, tone, message }]);
      // Auto-dismiss; manual dismiss can also remove it earlier (the filter is id-safe).
      setTimeout(() => dismiss(id), DISMISS_MS[tone]);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
    }),
    [push],
  );

  const polite = toasts.filter((toast) => toast.tone === 'success');
  const assertive = toasts.filter((toast) => toast.tone === 'error');

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 top-3 z-[60] flex flex-col items-center gap-2 px-4">
        {/* Two stacked live regions so polite/assertive announcements are routed correctly. */}
        <ToastRegion
          toasts={polite}
          live="polite"
          role="status"
          onDismiss={dismiss}
          dismissLabel={t('actions.close')}
        />
        <ToastRegion
          toasts={assertive}
          live="assertive"
          role="alert"
          onDismiss={dismiss}
          dismissLabel={t('actions.close')}
        />
      </div>
    </ToastContext.Provider>
  );
}

function ToastRegion({
  toasts,
  live,
  role,
  onDismiss,
  dismissLabel,
}: {
  toasts: readonly Toast[];
  live: 'polite' | 'assertive';
  role: 'status' | 'alert';
  onDismiss: (id: number) => void;
  dismissLabel: string;
}): React.ReactElement {
  return (
    <div aria-live={live} role={role} className="flex w-full max-w-sm flex-col items-stretch gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'toast-enter pointer-events-auto flex items-start gap-3 rounded-card border bg-surface p-3 text-text shadow-lg',
            toast.tone === 'success' ? 'border-success' : 'border-danger',
          )}
        >
          <span className="text-lg leading-none" aria-hidden="true">
            {toast.tone === 'success' ? '🎉' : '🌧️'}
          </span>
          <span className="min-w-0 flex-1 text-sm font-semibold">{toast.message}</span>
          <button
            type="button"
            onClick={() => onDismiss(toast.id)}
            aria-label={dismissLabel}
            className="rounded-md p-0.5 text-text-muted outline-none transition-colors hover:text-text focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span aria-hidden="true">{'✕'}</span>
          </button>
        </div>
      ))}
    </div>
  );
}
